import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  InlineEditor,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import IToolBarConfig from "@hid-galaxy-ui/galaxy-react/components/InlineEditor/IToolbarConfig";
import { AddPartnerLicenceDetailsType } from "../../../../../../../redux/saga/partner-program/licence-management/saga-actions";
import { useAppSelector } from "../../../../../../../redux";
import "./PartnerLicenceModal.scss";

const licenceInlineEditorToolBarConfig: IToolBarConfig = {
  headingConfig: {
    isDisplayHeading: true,
    headings: [false, 1, 2, 3, 4, 5, 6],
  },
  fontStyleConfig: {
    isDisplayBold: true,
    isDisplayItalic: true,
    isDisplayUnderline: true,
    isDisplayStrike: true,
  },
  indentConfig: {
    isDisplayOrdered: true,
    isDisplayBullet: true,
    isDisplayIndent: false,
  },
  colorConfig: {
    isDisplayBackground: false,
    isDisplayColor: false,
  },
  alignmentConfig: {
    isDisplayJustify: false,
    isDisplayCenter: false,
    isDisplayRight: false,
  },
  miscConfig: {
    isDisplayImage: false,
    isDisplayLink: false,
    isDisplayCodeblock: false,
    isDisplayBlockquote: false,
  },
  buttonConfig: {
    buttonLabel: "",
    displayPostButton: false,
    isRequired: false,
  },
};

type Props = {
  onClose: () => void;
  onSubmit: (payload: AddPartnerLicenceDetailsType) => void;
  open?: boolean;
  mode?: "add" | "edit";
  initialValue?: Partial<AddPartnerLicenceDetailsType>;
  busy?: boolean;
};

const PartnerLicenceModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  open = true,
  mode = "add",
  initialValue,
  busy = false,
}) => {
  const [form, setForm] = React.useState<AddPartnerLicenceDetailsType>({
    ID: initialValue?.ID ?? 0,
    license_type: initialValue?.license_type ?? "",
    license_count: Number(initialValue?.license_count ?? 0),
    points: Number(initialValue?.points ?? 0),
    comments: initialValue?.comments ?? "",
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const {
    status: {
      addPartnerLicenceStatus,
      addPartnerLicenceLoading,
      addPartnerLicenceMessage,
      updatePartnerLicenceLoading,
      updatePartnerLicenceStatus,
      updatePartnerLicenceMessage,
    },
  } = useAppSelector((state) => state.partner_licenceProgram);

  React.useEffect(() => {
    if (!open) return;
    setForm({
      ID: initialValue?.ID ?? 0,
      license_type: initialValue?.license_type ?? "",
      license_count: Number(initialValue?.license_count ?? 0),
      points: Number(initialValue?.points ?? 0),
      comments: initialValue?.comments ?? "",
    });
    setErrors({});
  }, [open, initialValue]);

  const validate = () => {
    const e: Record<string, string> = {};

    if (!form.license_type.trim()) {
      e.license_type = "License Type is required";
    }

    const count = Number(form.license_count);
    if (!Number.isFinite(count) || count < 0) {
      e.license_count = "License Count must be a non-negative number";
    } else if (count === 0) {
      e.license_count = "License Count is required";
    }


    const pts = Number(form.points);
    if (!Number.isFinite(pts) || pts < 0) {
      e.points = "License Points must be a non-negative number";
    }

    if (!form.comments.trim()) {
      e.comments = "Comment is required";
    } else if (form.comments.length > 100) {
      e.comments = "Comment must be at most 100 characters";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitted(true);

    const payload: AddPartnerLicenceDetailsType = {
      ID: form.ID,
      license_type: form.license_type.trim(),
      license_count: Number(form.license_count),
      points: Number(form.points),
      comments: form.comments.trim(),
    };

    onSubmit(payload);
  };
  const title = mode === "add" ? "Add New License" : "Edit License";
  const subtitle = "Configure a new license type";
  const ctaLabel = mode === "add" ? "ADD LICENSE" : "SAVE CHANGES";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="license-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {(!submitted || addPartnerLicenceLoading || updatePartnerLicenceLoading) &&
        <DialogTitle
          className="dialog-title"

        >
          <Box sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}> 
          <Box>
              <Typography
                className="title-text"

              >
                {title}
              </Typography>
              <Typography
                className="subtitle-text"
              >
                {subtitle}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}>
              <Iconography icon="xmark" />
            </IconButton>
          </Box>
        </DialogTitle>
      }
      {submitted ? (
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
          style={{ padding: "24px" }}
        >
          {(addPartnerLicenceLoading || updatePartnerLicenceLoading) && (
            <Loader variant={LoaderSizes.medium} />
          )}
          {mode === "add" && !addPartnerLicenceLoading && (
            <>
              <Iconography
                icon={addPartnerLicenceStatus ? "circleCheck" : "circleXmark"}
                size={IconographySizes.XLarge}
                theme={
                  addPartnerLicenceStatus
                    ? IconographyThemes.Success
                    : IconographyThemes.Error
                }
              />
              <Typography variant="body2">
                {addPartnerLicenceMessage ||
                  (addPartnerLicenceStatus
                    ? "License added successfully"
                    : "Failed to add license")}
              </Typography>
              <Button
                label="Close"
                onClick={onClose}
                variant="primary"
              />
            </>
          )}
          {mode === "edit" && !updatePartnerLicenceLoading && (
            <>
              <Iconography
                icon={
                  updatePartnerLicenceStatus ? "circleCheck" : "circleXmark"
                }
                size={IconographySizes.XLarge}
                theme={
                  updatePartnerLicenceStatus
                    ? IconographyThemes.Success
                    : IconographyThemes.Error
                }
              />
              <Typography variant="body2">
                {updatePartnerLicenceMessage ||
                  (updatePartnerLicenceStatus
                    ? "License updated successfully"
                    : "Failed to update license")}
              </Typography>
              <Button
                label="Close"
                onClick={onClose}
                variant="primary"
              />
            </>
          )}
        </DialogContent>
      ) : (
        <>
          <DialogContent className="dialog-content">
            <Box className="form-field">
              <label className="field-label">License Type</label>
              <TextField
                placeholder="e.g., Level 1"
                value={form.license_type}
                onChange={(e) =>
                  setForm({ ...form, license_type: e.target.value })
                }
                fullWidth
                variant="outlined"
                inputProps={{ "aria-label": "License Type" }}
                error={!!errors.license_type}
                helperText={errors.license_type || ""}
              />
            </Box>
            <Box
              className="form-row"
            >
              <Box className="form-field">
                <label className="field-label">License Count</label>
                <TextField
                  type="number"
                  value={form.license_count}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      license_count: Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 0, "aria-label": "License Count" }}
                  variant="outlined"
                  error={!!errors.license_count}
                  helperText={errors.license_count || ""}
                  placeholder="1000"
                  fullWidth
                />
              </Box>
              <Box className="form-field">
                <label className="field-label">License Points</label>
                <TextField
                  type="number"
                  value={form.points}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      points: Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 0, "aria-label": "License Points" }}
                  variant="outlined"
                  error={!!errors.points}
                  helperText={errors.points || ""}
                  fullWidth
                  placeholder="4999"
                />
              </Box>
            </Box>
            <Box className="form-field">
              <Box className="field-label-row">
                <label className="field-label">Comment</label>
                <span className="char-count">
                  {(form.comments || "").length}/100
                </span>
              </Box>
              <Box className="license-inline-editor-wrap">
                <InlineEditor
                  id="license-comment"
                  placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam"
                  toolBarConfig={licenceInlineEditorToolBarConfig}
                  editorData={form.comments}
                  handleOnChange={(value: string) =>
                    setForm((prev) => ({
                      ...prev,
                      comments:
                        value.length <= 100 ? value : prev.comments,
                    }))
                  }
                />
              </Box>
              {errors.comments && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {errors.comments}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions className="dialog-actions">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={onClose}
              disabled={busy}
            />
            <Button
              variant="primary"
              label={ctaLabel}
              onClick={handleSubmit}
              disabled={busy}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PartnerLicenceModal;
